import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { DataTable, type DataColumn } from '@/shared/ui/DataTable';
import { DetailBox } from '@/shared/ui/DetailLayout';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { PromoConditionModal } from './PromoConditionModal';
import {
  conditionToValues,
  emptyPromoCondition,
  expireInDays,
  fetchConditionActions,
  fetchPromoConditions,
  isRelativeExpire,
  type PromoCondition,
} from './promoSectionsApi';

type Dialog = { kind: 'create' } | { kind: 'edit'; item: PromoCondition } | null;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term'] as const;

const formatExpire = (expireTime: number | null | undefined) => {
  if (!expireTime) return '';
  return isRelativeExpire(expireTime) ? expireInDays(expireTime) : formatUnixDateTime(expireTime);
};

export const PromoConditionsSection = ({ promoId }: { promoId: number }) => {
  const [dialog, setDialog] = useState<Dialog>(null);

  const list = useQuery({
    queryKey: ['promo-conditions', promoId],
    queryFn: () => fetchPromoConditions(promoId),
  });
  const actions = useQuery({
    queryKey: ['promo-condition-actions', promoId],
    queryFn: () => fetchConditionActions(promoId),
  });

  const actionOptions = (actions.data ?? []).map(({ action, description }) => ({
    value: action,
    label: description,
  }));

  const columns: DataColumn<PromoCondition>[] = [
    { header: 'ID', render: (item) => item.id },
    { header: 'Promo ID', render: (item) => item.promotion_id },
    { header: 'Время жизни начисленных бонусов', render: (item) => formatExpire(item.expire_time) },
    { header: 'Ссылка', render: (item) => item.promotion_link },
    { header: 'Валюта', render: (item) => item.type_bonus },
    { header: 'Cумма бонуса', render: (item) => item.bonus },
    { header: 'Кол-во использований одним пользователем', render: (item) => item.count_use },
    {
      header: 'utm',
      render: (item) =>
        UTM_KEYS.map((key) => (
          <div key={key}>
            <strong>{key}:</strong> {item.utm?.[key]}
          </div>
        )),
    },
    { header: 'Тип пользователя', render: (item) => item.user_type?.description },
    {
      header: 'Edit',
      render: (item) => (
        <button type="button" className="btn btn-success btn-sm" onClick={() => setDialog({ kind: 'edit', item })}>
          <i className="fa fa-pencil" />
        </button>
      ),
    },
  ];

  const close = () => setDialog(null);

  return (
    <DetailBox title="Условия">
      <div className="clearfix" style={{ marginBottom: 12 }}>
        <button type="button" className="btn btn-info pull-right" onClick={() => setDialog({ kind: 'create' })}>
          Создать условие
        </button>
      </div>
      <QueryStatus isLoading={list.isLoading} error={list.error} errorText="Ошибка загрузки условий" />
      <DataTable columns={columns} rows={list.data ?? []} rowKey={(item) => item.id} />

      {dialog?.kind === 'create' && (
        <PromoConditionModal promoId={promoId} initial={emptyPromoCondition()} actions={actionOptions} onClose={close} />
      )}
      {dialog?.kind === 'edit' && (
        <PromoConditionModal
          promoId={promoId}
          id={dialog.item.id}
          initial={conditionToValues(dialog.item)}
          actions={actionOptions}
          onClose={close}
        />
      )}
    </DetailBox>
  );
};
